// ============================================================================
// TenantCostPanel — self-fetching cost view for a single tenant.
//
// The full "Costs tab" experience: daily/monthly stacked-bar chart, category
// summary legend, per-feature LLM breakdown, and the paginated line-item
// drill-down. Fetches everything itself from just a tenantId, so it can be
// dropped into the tenant detail page AND inline-expanded inside the
// Cost Tracking → Per-tenant usage table without prop threading.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  fetchTenantCostsDaily,
  fetchTenantCostsMonthly,
  fetchTenantCostsRollup,
  fetchTenantFeatureCosts,
  fetchTenantCostLineItems,
  type TenantCostDailyRow,
  type TenantCostMonthlyRow,
  type TenantCostRow,
  type TenantFeatureCostRow,
  type TenantCostLineItem,
} from '@/lib/api';

export function TenantCostPanel({
  tenantId,
  days = 30,
  rangeLabel = 'Last 30 days',
}: {
  tenantId: string;
  days?: number;
  rangeLabel?: string;
}) {
  const [daily, setDaily] = useState<TenantCostDailyRow[]>([]);
  const [monthly, setMonthly] = useState<TenantCostMonthlyRow[]>([]);
  const [summary, setSummary] = useState<TenantCostRow | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchTenantCostsDaily(tenantId, days),
      fetchTenantCostsMonthly(tenantId, 12),
      fetchTenantCostsRollup(days).then(rows => rows.find(r => r.tenant_id === tenantId) ?? null),
    ])
      .then(([d, m, s]) => { if (cancelled) return; setDaily(d); setMonthly(m); setSummary(s); })
      .catch(() => { if (cancelled) return; setDaily([]); setMonthly([]); setSummary(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, days]);

  const totalCents = summary?.total_cents ?? 0;
  const yearTotal  = monthly.reduce((sum, m) => sum + m.total_cents, 0);

  const [granularity, setGranularity] = useState<'daily' | 'monthly'>('daily');
  const [showLineItems, setShowLineItems] = useState(false);

  const palette = {
    llm:   'bg-violet-500',
    image: 'bg-amber-500',
    video: 'bg-rose-500',
    audio: 'bg-sky-500',
    other: 'bg-emerald-500',
  } as const;

  const bars = granularity === 'daily'
    ? daily.map(d => ({
        key: d.bucket_date, label: d.bucket_date,
        llm: d.llm_cents, image: d.image_cents, video: d.video_cents,
        audio: d.audio_cents, other: d.other_cents, total: d.total_cents,
      }))
    : monthly.map(m => ({
        key: m.bucket_month, label: monthLabel(m.bucket_month),
        llm: m.llm_cents, image: m.image_cents, video: m.video_cents,
        audio: m.audio_cents, other: m.other_cents, total: m.total_cents,
      }));
  const maxBar = Math.max(1, ...bars.map(b => b.total));

  return (
    <div className="glass-panel p-5">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-surface-900">
          Cost{granularity === 'daily' ? ` · ${rangeLabel}` : ' · 12 months'}
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-surface-500" title="Trailing 12 months total">
            12-mo: <span className="font-mono font-semibold text-surface-900">${(yearTotal / 100).toFixed(2)}</span>
          </span>
          {granularity === 'daily' && (
            <span className="text-xs text-surface-500">
              {loading ? 'Updating…' : `$${(totalCents / 100).toFixed(2)} window`}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 mb-3 text-[11px]">
        <button
          onClick={() => setGranularity('daily')}
          className={'px-2 py-0.5 rounded-md transition-colors ' +
            (granularity === 'daily' ? 'bg-primary text-white font-semibold' : 'text-surface-500 hover:text-surface-900 hover:bg-surface-100')}
        >Daily</button>
        <button
          onClick={() => setGranularity('monthly')}
          className={'px-2 py-0.5 rounded-md transition-colors ' +
            (granularity === 'monthly' ? 'bg-primary text-white font-semibold' : 'text-surface-500 hover:text-surface-900 hover:bg-surface-100')}
        >Monthly</button>
      </div>

      {bars.length === 0 ? (
        <p className="text-xs text-surface-500 py-4">
          {loading ? 'Loading…' : 'No priced events in this window.'}
        </p>
      ) : (
        <>
          <div className="flex items-end gap-1 h-24">
            {bars.map(b => {
              const h = (b.total / maxBar) * 100;
              const segments: Array<{ cls: string; cents: number }> = [
                { cls: palette.llm,   cents: b.llm },
                { cls: palette.image, cents: b.image },
                { cls: palette.video, cents: b.video },
                { cls: palette.audio, cents: b.audio },
                { cls: palette.other, cents: b.other },
              ].filter(s => s.cents > 0);
              return (
                <div
                  key={b.key}
                  className="flex-1 flex flex-col-reverse h-full transition-all"
                  title={`${b.label}: $${(b.total / 100).toFixed(2)}`}
                  style={{ height: '100%' }}
                >
                  <div className="w-full flex flex-col-reverse" style={{ height: `${h}%`, minHeight: '2px' }}>
                    {segments.map((s, j) => (
                      <div
                        key={j}
                        className={s.cls + ' transition-all'}
                        style={{ height: `${(s.cents / b.total) * 100}%`, minHeight: '1px' }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-[10px] text-surface-500">{bars[0]?.label}</span>
            <span className="text-[10px] text-surface-500">{bars[bars.length - 1]?.label}</span>
          </div>

          {summary && (
            <ul className="mt-4 space-y-1.5 text-xs">
              <CostLegendRow color={palette.llm}   label={`LLM (${summary.llm_request_count} calls)`} cents={summary.llm_cents} />
              <CostLegendRow color={palette.image} label="Image"                                       cents={summary.image_cents} />
              <CostLegendRow color={palette.video} label="Video"                                       cents={summary.video_cents} />
              <CostLegendRow color={palette.audio} label="Audio"                                       cents={summary.audio_cents} />
              <CostLegendRow color={palette.other} label="Other third-party"                           cents={summary.other_3p_cents} />
            </ul>
          )}

          <CostFeatureBreakdown tenantId={tenantId} days={days} />

          <button
            onClick={() => setShowLineItems(s => !s)}
            className="mt-4 text-[11px] font-semibold text-primary-600 hover:text-primary-700 transition-colors"
          >
            {showLineItems ? 'Hide' : 'Show'} charges · who did what
          </button>
          {showLineItems && (
            <CostLineItems tenantId={tenantId} days={days} />
          )}
        </>
      )}
    </div>
  );
}

// Per-feature LLM token + cost table for one tenant. Self-fetches.
function CostFeatureBreakdown({ tenantId, days }: { tenantId: string; days: number }) {
  const [rows, setRows] = useState<TenantFeatureCostRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTenantFeatureCosts(tenantId, days)
      .then(r => { if (!cancelled) setRows(r); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, days]);

  if (loading && rows.length === 0) {
    return <p className="mt-4 text-[11px] text-surface-400">Loading feature breakdown…</p>;
  }
  if (rows.length === 0) return null;

  const fmt = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `${(n / 1_000).toFixed(1)}k` : `${n}`;

  return (
    <div className="mt-4 border-t border-surface-200 pt-3">
      <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wide mb-2">
        By feature · LLM
      </h4>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-surface-200 text-left text-surface-500 font-medium">
              <th className="py-1 px-2">Feature</th>
              <th className="py-1 px-2 text-right">Calls</th>
              <th className="py-1 px-2 text-right">Tokens in</th>
              <th className="py-1 px-2 text-right">Tokens out</th>
              <th className="py-1 px-2 text-right">Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f, i) => (
              <tr key={`${f.feature}-${i}`} className="border-b border-surface-100 last:border-b-0">
                <td className="py-1 px-2 text-surface-700 truncate max-w-[180px]" title={f.feature}>{f.feature}</td>
                <td className="py-1 px-2 text-right font-mono text-surface-600">{f.request_count.toLocaleString()}</td>
                <td className="py-1 px-2 text-right font-mono text-surface-600">{fmt(f.prompt_tokens)}</td>
                <td className="py-1 px-2 text-right font-mono text-surface-600">{fmt(f.completion_tokens)}</td>
                <td className="py-1 px-2 text-right font-mono font-semibold text-surface-900">
                  {f.cost_cents > 0 ? `$${(f.cost_cents / 100).toFixed(2)}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function monthLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// Paginated line-item drill-down — who did what.
function CostLineItems({ tenantId, days }: { tenantId: string; days: number }) {
  const [items, setItems] = useState<TenantCostLineItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTenantCostLineItems(tenantId, { days, limit: pageSize, offset: page * pageSize })
      .then(({ items, total_count }) => {
        if (cancelled) return;
        setItems(items);
        setTotalCount(total_count);
      })
      .catch(() => { if (!cancelled) { setItems([]); setTotalCount(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, days, page]);

  const lastPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);

  return (
    <div className="mt-3 border-t border-surface-200 pt-3">
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wide">
          Charges {loading ? '…' : `(${totalCount.toLocaleString()})`}
        </h4>
        {totalCount > pageSize && (
          <div className="flex items-center gap-1.5 text-[11px]">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-2 py-0.5 rounded text-surface-600 hover:bg-surface-100 disabled:opacity-40">‹</button>
            <span className="text-surface-500">{page + 1} / {lastPage + 1}</span>
            <button onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
              className="px-2 py-0.5 rounded text-surface-600 hover:bg-surface-100 disabled:opacity-40">›</button>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] text-surface-500 py-2">No charges in this window.</p>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-surface-200 text-left text-surface-500 font-medium">
                <th className="py-1 px-2">When</th>
                <th className="py-1 px-2">Who</th>
                <th className="py-1 px-2">Provider · Model</th>
                <th className="py-1 px-2">Feature / Service</th>
                <th className="py-1 px-2 text-right">Units</th>
                <th className="py-1 px-2 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={`${it.occurred_at}-${i}`} className="border-b border-surface-100 last:border-b-0">
                  <td className="py-1 px-2 text-surface-600 whitespace-nowrap" title={new Date(it.occurred_at).toLocaleString()}>
                    {new Date(it.occurred_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </td>
                  <td className="py-1 px-2 text-surface-700 truncate max-w-[160px]" title={it.user_email ?? it.user_id ?? ''}>
                    {it.user_email ?? (it.user_id ? it.user_id.slice(0, 8) + '…' : '—')}
                  </td>
                  <td className="py-1 px-2 font-mono text-surface-700">
                    {it.provider}{it.model ? ` · ${it.model}` : ''}
                  </td>
                  <td className="py-1 px-2 text-surface-700 truncate max-w-[140px]" title={it.service_or_feature}>
                    {it.service_or_feature}
                  </td>
                  <td className="py-1 px-2 text-right font-mono text-surface-600">
                    {it.units != null ? Number(it.units).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}
                  </td>
                  <td className="py-1 px-2 text-right font-mono font-semibold text-surface-900">
                    {it.cost_usd_cents != null ? `$${(it.cost_usd_cents / 100).toFixed(2)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CostLegendRow({ color, label, cents }: { color: string; label: string; cents: number }) {
  if (cents <= 0) return null;
  return (
    <li className="flex items-center justify-between text-surface-700">
      <span className="flex items-center gap-2">
        <span className={'inline-block w-2 h-2 rounded-sm ' + color} />
        {label}
      </span>
      <span className="font-mono text-[11px]">${(cents / 100).toFixed(2)}</span>
    </li>
  );
}
